// Motion presets — one function per pattern in the motion-stack guide.
// Every preset:
//   • Uses the standard Voyantis ease (override via opts.ease)
//   • Uses the standard "top bottom-=100" trigger (override via opts.start)
//   • Plays once and stays (override via opts.once: false)
//   • Respects prefers-reduced-motion (jumps to final state)
//
// Requires: gsap, ScrollTrigger (and CustomEase for project eases).

import { ease, VOY_EASE } from './easings.js';
import { scrollTriggerConfig, prefersReducedMotion } from './trigger.js';

/**
 * Resolve target(s) — accepts an Element, NodeList, Array, or selector string.
 * Returns an array (possibly empty).
 */
function resolve(target) {
  if (!target) return [];
  if (typeof target === 'string') return Array.from(document.querySelectorAll(target));
  if (target instanceof Element) return [target];
  if (target.length !== undefined) return Array.from(target);
  return [];
}

// ─── Pattern #2 — Scroll Reveal (fade up) ────────────────────────────────────
/**
 * Fade an element up into view as it enters the viewport.
 * Default: opacity 0→1, y 24→0, 1.0s, voyEase, fires at "top bottom-=100".
 *
 *   fadeUp('.hp-section_h2');
 *   fadeUp(el, { y: 16, duration: 0.8 });
 */
export function fadeUp(target, opts = {}) {
  const els = resolve(target);
  if (!els.length) return;

  const {
    y = 24,
    opacity = 1,
    duration = 1.0,
    delay = 0,
    ease: easeName = VOY_EASE,
    trigger,
    start,
    once = true,
  } = opts;

  els.forEach((el) => {
    gsap.set(el, { opacity: 0, y });

    if (prefersReducedMotion()) {
      gsap.set(el, { opacity, y: 0 });
      return;
    }

    gsap.to(el, {
      opacity,
      y: 0,
      duration,
      delay,
      ease: ease(easeName),
      scrollTrigger: scrollTriggerConfig({ trigger: trigger || el, start, once }),
    });
  });
}

// ─── Pattern #6 — Staggered List ─────────────────────────────────────────────
/**
 * Reveal a list of elements with a staggered cascade.
 * Default: per-item fade up 16px, 0.9s, 0.12s gap, fires when wrapper enters view.
 *
 *   staggerList('.hp-cards .card');
 *   staggerList(items, { gap: 0.08, y: 12 });
 */
export function staggerList(target, opts = {}) {
  const els = resolve(target);
  if (!els.length) return;

  const {
    y = 16,
    duration = 0.9,
    gap = 0.12,
    ease: easeName = VOY_EASE,
    trigger,
    start,
    once = true,
  } = opts;

  // Use the first element's parent as the trigger by default — fires when
  // the group enters view, then cascades through children.
  const triggerEl = trigger || els[0].parentElement || els[0];

  gsap.set(els, { opacity: 0, y });

  if (prefersReducedMotion()) {
    gsap.set(els, { opacity: 1, y: 0 });
    return;
  }

  gsap.to(els, {
    opacity: 1,
    y: 0,
    duration,
    ease: ease(easeName),
    stagger: gap,
    scrollTrigger: scrollTriggerConfig({ trigger: triggerEl, start, once }),
  });
}

// ─── Pattern #3 — Line / Divider Reveal ──────────────────────────────────────
/**
 * Grow a vertical line from its origin to full height.
 * Default: scaleY 0→1 from top, 0.8s.
 *
 *   lineReveal('.section-divider');
 *   lineReveal(el, { origin: 'center', duration: 0.6 });
 */
export function lineReveal(target, opts = {}) {
  const els = resolve(target);
  if (!els.length) return;

  const {
    origin = 'top',
    duration = 0.8,
    ease: easeName = VOY_EASE,
    trigger,
    start,
    once = true,
  } = opts;

  els.forEach((el) => {
    gsap.set(el, { scaleY: 0, transformOrigin: `50% ${origin}` });

    if (prefersReducedMotion()) {
      gsap.set(el, { scaleY: 1 });
      return;
    }

    gsap.to(el, {
      scaleY: 1,
      duration,
      ease: ease(easeName),
      scrollTrigger: scrollTriggerConfig({ trigger: trigger || el, start, once }),
    });
  });
}

// ─── Pattern #4 — Image Reveal (clip-path) ───────────────────────────────────
/**
 * Open an image from a cropped state to full visibility (cinematic).
 * Default: clipPath inset 20% → 0%, opacity 0→1, 1.4s, voyEase.
 *
 *   imageReveal('.hero-image');
 */
export function imageReveal(target, opts = {}) {
  const els = resolve(target);
  if (!els.length) return;

  const {
    inset = 20,
    duration = 1.4,
    ease: easeName = VOY_EASE,
    trigger,
    start,
    once = true,
  } = opts;

  els.forEach((el) => {
    gsap.set(el, { opacity: 0, clipPath: `inset(${inset}% ${inset}% ${inset}% ${inset}%)` });

    if (prefersReducedMotion()) {
      gsap.set(el, { opacity: 1, clipPath: 'inset(0% 0% 0% 0%)' });
      return;
    }

    gsap.to(el, {
      opacity: 1,
      clipPath: 'inset(0% 0% 0% 0%)',
      duration,
      ease: ease(easeName),
      scrollTrigger: scrollTriggerConfig({ trigger: trigger || el, start, once }),
    });
  });
}

// ─── Pattern #5 — Parallax (scroll-linked) ───────────────────────────────────
/**
 * Drift an element with the scroll. Subtle by default (12% range).
 * Note: parallax IS scroll-linked, so it ignores `once` and uses scrub.
 *
 *   parallax('.hero-bg', { trigger: '.hero', amount: 12 });
 */
export function parallax(target, opts = {}) {
  const els = resolve(target);
  if (!els.length) return;

  const {
    amount = 12, // percent of element height
    scale: scaleEnd = 1.05,
    trigger,
    start = 'top bottom',
    end = 'bottom top',
    scrub = true,
  } = opts;

  if (prefersReducedMotion()) return;

  els.forEach((el) => {
    gsap.fromTo(
      el,
      { yPercent: 0, scale: 1 },
      {
        yPercent: amount,
        scale: scaleEnd,
        ease: 'none',
        scrollTrigger: { trigger: trigger || el, start, end, scrub },
      }
    );
  });
}

// ─── Pattern #7 — Opacity-Only (labels / metadata) ───────────────────────────
/**
 * Fade in supporting text without movement. Targets reduced opacity (0.6 by
 * default) since these elements are meant to feel subdued.
 *
 *   opacityFade('.hp_label', { opacity: 0.7 });
 */
export function opacityFade(target, opts = {}) {
  const els = resolve(target);
  if (!els.length) return;

  const {
    opacity = 0.6,
    duration = 0.8,
    delay = 0,
    ease: easeName = VOY_EASE,
    trigger,
    start,
    once = true,
  } = opts;

  els.forEach((el) => {
    gsap.set(el, { opacity: 0 });

    if (prefersReducedMotion()) {
      gsap.set(el, { opacity });
      return;
    }

    gsap.to(el, {
      opacity,
      duration,
      delay,
      ease: ease(easeName),
      scrollTrigger: scrollTriggerConfig({ trigger: trigger || el, start, once }),
    });
  });
}

// ─── Pattern #1 — Hero Entrance Sequence ─────────────────────────────────────
/**
 * Cascade hero elements in on page load (no ScrollTrigger).
 * Pass an array of { target, y, duration, delay, ease, opacity }; defaults
 * follow the guide's hero spec (title 1.2s no delay, subtitle 1.0s 0.3s,
 * CTA 1.0s 0.55s, etc.). Returns the timeline so callers can extend it.
 *
 *   heroSequence([
 *     { target: titleEl,    y: 24, duration: 1.2, delay: 0    },
 *     { target: subtitleEl, y: 16, duration: 1.0, delay: 0.3  },
 *     { target: ctaEl,      y: 16, duration: 1.0, delay: 0.55 },
 *   ]);
 */
export function heroSequence(steps = [], opts = {}) {
  const { ease: easeName = VOY_EASE } = opts;
  const tl = gsap.timeline({ defaults: { overwrite: 'auto', ease: ease(easeName) } });

  steps.forEach((step) => {
    const el = step.target instanceof Element ? step.target : document.querySelector(step.target);
    if (!el) return;

    const { y = 24, opacity = 1, duration = 1.0, delay = 0 } = step;
    gsap.set(el, { opacity: 0, y });

    if (prefersReducedMotion()) {
      gsap.set(el, { opacity, y: 0 });
      return;
    }

    tl.to(el, { opacity, y: 0, duration }, delay);
  });

  return tl;
}
